import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { authRoutes } from "app/router/auth/routes";
import { homeSectionIds } from "shared/config/site";
import type {
  BreadcrumbItem,
  NavigationActionItem,
} from "shared/navigation/types";
import { openHomeSection } from "shared/lib/scroll";
import {
  governanceNavigationLinks,
  institutionalNavigationLinks,
  investorNavigationLinks,
} from "app/router/public/navigation";
import type { PublicBreadcrumbLink } from "app/router/public/types";
import { usePageNavigation } from "app/router/public/useNavigation";

function mapNavigationLinksToActions(
  links: { label: string; page: Parameters<ReturnType<typeof usePageNavigation>>[0] }[],
  navigateToPage: ReturnType<typeof usePageNavigation>,
): NavigationActionItem[] {
  return links.map((link) => ({
    label: link.label,
    onClick: () => navigateToPage(link.page),
  }));
}

export function buildBreadcrumbs(
  onNavigate: ReturnType<typeof usePageNavigation> | undefined,
  links: PublicBreadcrumbLink[],
): BreadcrumbItem[] {
  return links.map((item) => {
    if (!item.page) {
      return { label: item.label };
    }

    const page = item.page;

    return {
      label: item.label,
      onClick: () => onNavigate?.(page),
    };
  });
}

export function useShellNavigation() {
  const navigate = useNavigate();
  const navigateToPage = usePageNavigation();

  const navigateToHome = useCallback(() => {
    navigateToPage("home");
  }, [navigateToPage]);

  const openHomeHero = useCallback(() => {
    openHomeSection(navigateToHome, homeSectionIds.hero);
  }, [navigateToHome]);

  const openSolutionsSection = useCallback(() => {
    openHomeSection(navigateToHome, homeSectionIds.solutions);
  }, [navigateToHome]);

  const primaryLinks = useMemo(
    () =>
      [
        {
          label: "Nossas soluções",
          onClick: openSolutionsSection,
        },
        {
          label: "Quem somos",
          onClick: () => navigateToPage("quem-somos"),
        },
      ] satisfies NavigationActionItem[],
    [navigateToPage, openSolutionsSection],
  );

  const investorLinks = useMemo(
    () => mapNavigationLinksToActions(investorNavigationLinks, navigateToPage),
    [navigateToPage],
  );

  const contactLink = useMemo(
    () =>
      ({
        label: "Fale conosco",
        onClick: () => navigateToPage("fale-conosco"),
      }) satisfies NavigationActionItem,
    [navigateToPage],
  );

  const clientAccessLink = useMemo(
    () =>
      ({
        label: "Acesso ao portal OXY",
        onClick: () => navigate(authRoutes.login.path),
      }) satisfies NavigationActionItem,
    [navigate],
  );

  const institutionalLinks = useMemo(
    () =>
      [
        ...mapNavigationLinksToActions(
          institutionalNavigationLinks,
          navigateToPage,
        ),
      ] satisfies NavigationActionItem[],
    [navigateToPage],
  );

  const governanceLinks = useMemo(
    () =>
      mapNavigationLinksToActions(
        governanceNavigationLinks,
        navigateToPage,
      ),
    [navigateToPage],
  );

  const siteHeaderProps = useMemo(
    () => ({
      brandAction: navigateToHome,
      primaryLinks,
      investorLinks,
      contactLink,
      clientAccessLink,
    }),
    [
      clientAccessLink,
      contactLink,
      investorLinks,
      navigateToHome,
      primaryLinks,
    ],
  );

  const siteFooterProps = useMemo(
    () => ({
      homeAction: openHomeHero,
      institutionalLinks,
      governanceLinks,
    }),
    [governanceLinks, institutionalLinks, openHomeHero],
  );

  return {
    siteHeaderProps,
    siteFooterProps,
  };
}
